import { useState, useCallback, useEffect } from 'react';
import { Issue, IssueFormData } from '../types/issue';
import { issueService } from '../services/issueService';
import { toast } from 'react-hot-toast';
import { useApp } from '../context/AppContext';

export const useIssues = () => {
  const [issues, setIssues] = useState<Issue[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const { isInitialized, initializeApp } = useApp();

  const fetchIssues = useCallback(async () => {
    if (isInitialized && issues.length > 0) {
      console.log('Выпуски уже загружены, пропускаем повторную загрузку');
      return;
    }

    console.log('Начало загрузки выпусков...');
    setLoading(true);
    setError(null);
    try {
      const response = await fetch('/api/issues');
      if (!response.ok) {
        throw new Error('Ошибка при загрузке выпусков');
      }
      const data = await response.json();
      console.log('Получены выпуски:', data);
      setIssues(data);
      if (!isInitialized) {
        initializeApp();
      }
    } catch (err) {
      console.error('Ошибка при загрузке выпусков:', err);
      setError(err instanceof Error ? err.message : 'Произошла ошибка при загрузке выпусков');
    } finally {
      setLoading(false);
    }
  }, [isInitialized, issues.length, initializeApp]);

  // Создание нового выпуска
  const createIssue = async (data: IssueFormData) => {
    try {
      const newIssue = await issueService.createIssue(data);
      setIssues(prev => [...prev, newIssue]);
      toast.success('Выпуск успешно создан');
      return newIssue;
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Ошибка при создании выпуска');
      toast.error('Ошибка при создании выпуска');
      throw err;
    }
  };

  // Обновление выпуска
  const updateIssue = async (id: number, data: IssueFormData) => {
    try {
      const updatedIssue = await issueService.updateIssue(id, data);
      setIssues(prev => prev.map(issue => issue.id === id ? updatedIssue : issue));
      toast.success('Выпуск успешно обновлен');
      return updatedIssue;
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Ошибка при обновлении выпуска');
      toast.error('Ошибка при обновлении выпуска');
      throw err;
    }
  };

  // Удаление выпуска
  const deleteIssue = async (id: number) => {
    try {
      await issueService.deleteIssue(id);
      setIssues(prev => prev.filter(issue => issue.id !== id));
      toast.success('Выпуск успешно удален');
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Ошибка при удалении выпуска');
      toast.error('Ошибка при удалении выпуска');
      throw err;
    }
  };

  // Публикация выпуска
  const publishIssue = async (id: number) => {
    try {
      const publishedIssue = await issueService.publishIssue(id);
      setIssues(prev => prev.map(issue => issue.id === id ? publishedIssue : issue));
      toast.success('Выпуск успешно опубликован');
      return publishedIssue;
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Ошибка при публикации выпуска');
      toast.error('Ошибка при публикации выпуска');
      throw err;
    }
  };

  // Архивирование выпуска
  const archiveIssue = async (id: number) => {
    try {
      const archivedIssue = await issueService.archiveIssue(id);
      setIssues(prev => prev.map(issue => issue.id === id ? archivedIssue : issue));
      toast.success('Выпуск успешно архивирован');
      return archivedIssue;
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Ошибка при архивации выпуска');
      toast.error('Ошибка при архивации выпуска');
      throw err;
    }
  };

  useEffect(() => {
    if (!isInitialized) {
    fetchIssues();
    }
  }, [fetchIssues, isInitialized]);

  return {
    issues,
    loading,
    error,
    createIssue,
    updateIssue,
    deleteIssue,
    publishIssue,
    archiveIssue,
    refreshIssues: fetchIssues
  };
}; 